import { type Entity, type Kyc, type OptIns, type Subject } from '~/domains/platform/infra/deus-ex-machina/ports'

import { type Assessment } from './entities/KYC'

export type KycRouteKind =
  | 'challenge-blocked'
  | 'risk-blocked'
  | 'analysis'
  | 'confirm-email'
  | 'select-account'
  | 'render'

export interface KycRoutePrefs {
  dontShowKycUpdateAssessment: string[]
  dontShowChallengeBlocked: string
}

export interface ResolveKycRouteOptions {
  assessments: Assessment[]
  subject: Subject<OptIns>
  entity: Entity<OptIns>
  hasNoAvailableIdentities: boolean
  currentPathname: string
  prefs: KycRoutePrefs
}

const KYC_STATUS = {
  pendingAnalysis: 'pending_analysis',
  pendingEmailConfirmation: 'pending_email_confirmation',
  rejected: 'rejected',
  approved: 'approved',
  pendingKyc: 'pending_kyc'
} as const satisfies Record<string, Kyc['status']>

export function requiresKycAnalysis(assessments: Assessment[]) {
  const deviceRegistration = assessments.some(({ reason }) => reason === 'device_registration')

  const requiresAnalysis = assessments.some(
    ({ isMandatory, reason, status }) =>
      reason !== 'update' &&
      ((isMandatory && reason !== 'account_request' && reason !== 'account_request_v2') || status === 'rejected')
  )

  return deviceRegistration || requiresAnalysis
}

export function resolveKycRoute(options: ResolveKycRouteOptions): { kind: KycRouteKind } {
  const { assessments, subject, entity, hasNoAvailableIdentities, currentPathname, prefs } = options
  const challengeBlocked =
    assessments.some(({ reason, status }) => reason === 'unblock_challenge' && status === 'requested') &&
    !prefs.dontShowChallengeBlocked.includes(subject.id)

  if (challengeBlocked) return { kind: 'challenge-blocked' }

  const riskAssessment = assessments.some(({ reason }) => reason === 'risk_analysis')

  if (riskAssessment) return { kind: 'risk-blocked' }

  if (requiresKycAnalysis(assessments)) return { kind: 'analysis' }

  if (entity.stoneAccount?.kyc?.status === KYC_STATUS.pendingEmailConfirmation) return { kind: 'confirm-email' }

  if (hasNoAvailableIdentities && currentPathname === '/home') return { kind: 'select-account' }

  return { kind: 'render' }
}
